/** Column math for the responsive card grid (`VirtualizedCards`), kept out of the component so it's unit-testable. */
import { useState } from 'react';

const GAP_REM = 1; // matches the `gap-4` / `pb-4` classes on the grid rows.

/**
 * Columns that fit a measured pixel width. Both the minimum column width and the grid gap are authored
 * in rem, so they're converted with the page's REAL rem — the browser's default font size is a user
 * accessibility setting. Assuming 16 packs an extra column for anyone who raised it, making the cards
 * narrower than `minColumnRem` for exactly the users who asked for more room.
 */
export function fitColumns(width: number, minColumnRem: number, maxColumns: number, rem: number): number {
  const minColPx = minColumnRem * rem;
  const gapPx = GAP_REM * rem;
  return Math.max(1, Math.min(maxColumns, Math.floor((width + gapPx) / (minColPx + gapPx))) || 1);
}

/** Real px size of 1rem. Read once — it's a browser preference, fixed for the session in practice. */
export function useRemSize(): number {
  const [rem] = useState(() => {
    const size = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return Number.isFinite(size) && size > 0 ? size : 16;
  });
  return rem;
}
